import {
  actions,
  apply,
  applyElement,
  isDocumentObserverActive,
  action as registerAction,
} from '@engine'
import { DATASTAR_READY_EVENT } from '@engine/consts'
import {
  computed as datastarComputed,
  effect,
  getPath,
  mergePaths,
  root,
} from '@engine/signals'
import type { HTMLOrSVG } from '@engine/types'
import { morph } from '@plugins/watchers/patchElements'
import { kebab, snake } from '@utils/text'
import {
  type CodecRegistry,
  codecRegistry,
  decodeCodec,
  getCodecDefault,
  getCodecManifest,
  type InferProps,
  type PropDefs,
} from './codecs'
import { initRocketConditionals } from './conditional'
import { initRocketFors } from './for'
import {
  appendComposedTemplateValue,
  hypertext,
  rewriteDataAttributes,
  rocketDispatchActionName,
  rocketRefAttr,
  type TaggedLiteral,
  type TaggedLiteralScope,
} from './template'

export const rocketHostAttr = 'data-rocket-host'
type AnyRecord = {
  [key: string]: any
}
type SetupSignal = (<T>(name: string, initialValue: T) => T) & AnyRecord
type RefRecord = Record<string, Element | undefined>
export type RefCtors = Record<string, abstract new (...args: any[]) => Element>
export type InstancesOf<C extends RefCtors> = {
  [K in keyof C]?: InstanceType<C[K]>
}
type StateRecord = AnyRecord
type PropOverrideGetter<
  Props extends Record<string, any>,
  Name extends keyof Props & string,
> = (getDefault: () => Props[Name]) => any
type PropOverrideSetter<
  Props extends Record<string, any>,
  Name extends keyof Props & string,
> = (value: any, setDefault: (value: Props[Name]) => void) => void
type HostPropDescriptor = Omit<PropertyDescriptor, 'configurable'>
export type RocketHost = HTMLElement & {
  dispatchRocketAction(
    name: string,
    el: Element | null,
    evt: Event | undefined,
    cleanups: Map<string, () => void>,
    ...args: any[]
  ): any
}

export type RocketHostWithProps<Props extends Record<string, any>> =
  RocketHost & Props

type SetupEmit = {
  (type: string): void
  (...types: [string, ...string[]]): void
  <Detail>(
    type: string,
    detail: Detail,
    options?: Omit<CustomEventInit<Detail>, 'detail'>,
  ): void
}

type SetupEmitCancellable = {
  (type: string): boolean
  <Detail>(
    type: string,
    detail: Detail,
    options?: Omit<CustomEventInit<Detail>, 'detail' | 'cancelable'>,
  ): boolean
}

type PropObserver<Props extends Record<string, any>> =
  | (() => void)
  | ((props: Props, changes: Partial<Props>) => void)

type SetupContext<Props extends Record<string, any>> = {
  props: Props

  // Global Datastar signal store exposed directly to setup code.
  //
  // This is the same reactive root object Datastar expressions read from when
  // they access `$foo`, `$bar.baz`, etc. Rocket-local state created via
  // `$$.foo = ...` still lives under the component's private `_rocket...` path;
  // `$` is for intentionally reaching shared/global state.
  $: Record<string, any>

  // Declares and accesses instance-local Rocket state through a callable
  // proxy.
  //
  // `$$.count = 0` creates `$$count` for templates and also enables
  // imperative reads/writes like `$$.count` and `$$.count += 1` inside
  // `setup`. Assigning a function like `$$.total = () => $$.count + 1`
  // creates a local computed signal. Returning a live primitive reference is
  // not possible in JavaScript, so the proxy lives on `$$` itself rather than
  // on the return value.
  $$: SetupSignal
  effect(fn: () => void): () => void
  apply(root: HTMLOrSVG | ShadowRoot, merge?: boolean): void
  adoptStyles(host: HTMLElement, ...styles: string[]): void
  cleanup(fn: () => void): void
  emit: SetupEmit
  emitCancellable: SetupEmitCancellable

  // Global Datastar actions exposed as imperative functions for setup code.
  //
  // This is intentionally separate from `action(name, fn)`: `actions` calls the
  // existing global registry (the same actions available to `@foo(...)`
  // expressions), while `action` registers component-local Rocket actions.
  actions: Record<string, (...args: any[]) => any>
  action(name: string, fn: RocketAction<Props>): void
  observeProps(fn: PropObserver<Props>, ...propNames: Array<keyof Props & string>): () => void
  overrideProp<Name extends keyof Props & string>(
    name: Name,
    getter?: PropOverrideGetter<Props, Name>,
    setter?: PropOverrideSetter<Props, Name>,
  ): void
  defineHostProp(name: string, descriptor: HostPropDescriptor): void
  render: SetupRender<Props>
  host: RocketHostWithProps<Props>
}

type FirstUpdateContext<
  Props extends Record<string, any>,
  Refs extends RefCtors = RefCtors,
> = SetupContext<Props> & {
  refs: InstancesOf<Refs>
}

type RenderContext<Props extends Record<string, any>> = {
  html: TaggedLiteral
  svg: TaggedLiteral
  props: Props
  host: RocketHostWithProps<Props>
}

type RenderContextOverrides<Props extends Record<string, any>> = Partial<
  RenderContext<Props>
>

type RocketPrimitiveRenderValue =
  | string
  | number
  | boolean
  | bigint
  | Date
  | null
  | undefined

type RocketComposedRenderValue =
  | RocketPrimitiveRenderValue
  | Node
  | Iterable<RocketComposedRenderValue>

type RocketRenderValue =
  | DocumentFragment
  | RocketPrimitiveRenderValue
  | Iterable<RocketComposedRenderValue>

type RocketRender<Props extends Record<string, any>> = (
  context: RenderContext<Props>,
  ...args: any[]
) => RocketRenderValue

type SetupRender<Props extends Record<string, any>> = (
  context: RenderContextOverrides<Props>,
  ...args: any[]
) => void

type RocketAction<Props extends Record<string, any>> = (
  context: {
    host: RocketHostWithProps<Props>
    props: Props
    state: StateRecord
    el: Element | null
    evt: Event | undefined
  },
  ...args: any[]
) => any

type RocketDefinition<
  Defs extends PropDefs = PropDefs,
  Refs extends RefCtors = RefCtors,
> = {
  refs?: Refs
  props?: (codecs: CodecRegistry) => Defs
  manifest?: RocketManifestMeta
  setup?: (context: SetupContext<InferProps<Defs>>) => void
  onFirstUpdate?: (context: FirstUpdateContext<InferProps<Defs>, Refs>) => void
  render?: RocketRender<InferProps<Defs>>
  mode?: 'open' | 'closed' | 'light'
  renderOnPropChange?:
    | boolean
    | ((context: {
        host: RocketHostWithProps<InferProps<Defs>>
        props: InferProps<Defs>
        changes: Partial<InferProps<Defs>>
      }) => boolean)
}

type RocketManifestMeta = {
  slots?: RocketManifestSlot[]
  events?: RocketManifestEvent[]
}

type RocketManifestSlot = {
  name: string
  description?: string
}

type RocketManifestEvent = {
  name: string
  kind?: 'event' | 'custom-event'
  bubbles?: boolean
  composed?: boolean
  description?: string
}

type RocketManifestProp = {
  name: string
  attribute: string
  type: string
  default: unknown
  required: boolean
  values?: readonly unknown[]
  docs?: {
    description?: string
    label?: string
    control?: 'auto' | 'text' | 'textarea' | 'number' | 'boolean' | 'select'
    placeholder?: string
  }
}

type RocketComponentManifest = {
  tag: string
  props: RocketManifestProp[]
  slots: RocketManifestSlot[]
  events: RocketManifestEvent[]
}

type RocketManifestDocument = {
  version: 1
  generatedAt: string
  components: RocketComponentManifest[]
}

type RocketPublishOptions = {
  endpoint: string
  headers?: Record<string, string>
}

type PropObserverRegistration<Props extends Record<string, any>> = {
  fn: PropObserver<Props>
  names: Set<keyof Props & string> | null
}

// Runtime surface used by Rocket's rewritten `@dispatchRocket(...)` calls.
//
// Datastar expressions only know about the DOM element currently being
// evaluated. Rocket stores a bridge for each relevant element in
// `rocketHostsByElement` so the dispatch action can recover the owning
// component instance and forward the call into that instance's local/global
// action resolution path.
type RocketActionBridge = RocketHost

// Bridge context adapter from Rocket runtime calls into the engine's global
// action contract.
//
// Reusing the same context contract keeps global actions behaving the same way
// whether they are called from normal Datastar expressions, from Rocket's
// `@dispatchRocket(...)` bridge, or imperatively via `setup({ actions })`.
const globalActionContext = (
  el: HTMLOrSVG,
  evt: Event | undefined,
  cleanups: Map<string, () => void>,
) => ({
  el,
  evt,
  error: (name: string, ctx?: Record<string, any>) => {
    const error = new Error(name)
    ;(error as Error & { ctx?: Record<string, any> }).ctx = ctx
    return error as never
  },
  cleanups,
})

const rocketDeferredIgnoreAttr = 'data-rocket-deferred-ignore'

// Guard unresolved Rocket hosts before custom-element upgrade.
//
// Some pages insert custom elements and host children containing raw `$$...`
// Datastar expressions before `customElements.define()` for that tag has run.
// The engine only knows how to skip work by reading DOM attributes, so we mark
// those hosts with a real `data-ignore` up front. Rocket later adopts that
// marker in the constructor and removes it after setup/render, which prevents
// the engine from evaluating host children before Rocket has rewritten `$$...`
// to the instance's concrete `_rocket...` signal path.
const markPendingRocketHosts = (root: ParentNode) => {
  const nodes =
    root instanceof Element
      ? [root, ...root.querySelectorAll('*')]
      : [...root.querySelectorAll('*')]
  for (const node of nodes) {
    if (!(node instanceof HTMLElement)) continue
    const tagName = node.tagName.toLowerCase()
    if (
      !tagName.includes('-') ||
      customElements.get(tagName) ||
      node.hasAttribute(rocketDeferredIgnoreAttr) ||
      node.hasAttribute('data-ignore')
    ) {
      continue
    }
    let needsDeferredIgnore = false
    for (const candidate of [node, ...node.querySelectorAll('*')]) {
      for (const attr of candidate.attributes) {
        if (attr.name.startsWith('data-') && attr.value.includes('$$')) {
          needsDeferredIgnore = true
          break
        }
      }
      if (needsDeferredIgnore) break
    }
    if (!needsDeferredIgnore) continue
    node.setAttribute('data-ignore', '')
    node.setAttribute(rocketDeferredIgnoreAttr, '')
  }
}

// Keep the pre-upgrade guard in sync with live DOM insertion.
//
// Rocket examples and backend patches can insert custom-element hosts after the
// initial document scan. Watching the document here ensures newly added
// unresolved hosts get the same temporary `data-ignore` guard before the engine
// can walk into their raw `$$...` children.
const observePendingRocketHosts = () => {
  markPendingRocketHosts(document)
  new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes' && record.target instanceof Element) {
        markPendingRocketHosts(record.target)
        continue
      }
      for (const node of record.addedNodes) {
        if (node instanceof Element) markPendingRocketHosts(node)
      }
    }
  }).observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
  })
}

let rocketBootstrapStarted = false
let rocketBootstrapWaiting = false
let pendingHostObservationStarted = false

// Queue `rocket(tag, options)` calls made before Datastar's document observer
// is ready. Rocket can accept registrations early, but custom-element setup
// must wait until the engine bootstrap path exists.
const pendingRocketDefinitions = new Map<string, RocketDefinition<any, any>>()

// Start the global Rocket bootstrap once, then flush any queued component
// registrations after Datastar is ready to observe/apply them.
const ensureRocketBootstrap = () => {
  if (!pendingHostObservationStarted) {
    pendingHostObservationStarted = true
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observePendingRocketHosts, {
        once: true,
      })
    } else {
      observePendingRocketHosts()
    }
  }
  if (rocketBootstrapStarted || rocketBootstrapWaiting) return
  if (isDocumentObserverActive()) {
    rocketBootstrapStarted = true
    if (pendingRocketDefinitions.size === 0) return
    for (const [tag, options] of pendingRocketDefinitions) {
      pendingRocketDefinitions.delete(tag)
      rocket(tag, options)
    }
    return
  }
  rocketBootstrapWaiting = true
  document.addEventListener(
    DATASTAR_READY_EVENT,
    () => {
      rocketBootstrapWaiting = false
      if (rocketBootstrapStarted) return
      rocketBootstrapStarted = true
      if (pendingRocketDefinitions.size === 0) return
      for (const [tag, options] of pendingRocketDefinitions) {
        pendingRocketDefinitions.delete(tag)
        rocket(tag, options)
      }
    },
    { once: true },
  )
}

ensureRocketBootstrap()

const registeredTags = new Set<string>()
const rocketManifestRegistry = new Map<string, RocketComponentManifest>()
let nextInstanceId = 0

const cloneRocketManifestComponent = (
  component: RocketComponentManifest,
): RocketComponentManifest => ({
  tag: component.tag,
  props: component.props.map((prop) => ({
    ...prop,
    values: prop.values ? [...prop.values] : undefined,
    docs: prop.docs ? { ...prop.docs } : undefined,
  })),
  slots: component.slots.map((slot) => ({ ...slot })),
  events: component.events.map((event) => ({ ...event })),
})

const rocketManifestDocument = (): RocketManifestDocument => ({
  version: 1,
  generatedAt: new Date().toISOString(),
  components: [...rocketManifestRegistry.values()]
    .map(cloneRocketManifestComponent)
    .sort((left, right) => left.tag.localeCompare(right.tag)),
})

export const publishRocketManifests = async ({
  endpoint,
  headers,
}: RocketPublishOptions) =>
  fetch(endpoint, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...headers,
    },
    body: JSON.stringify(rocketManifestDocument()),
  })

// Maps any DOM node that can originate a Rocket action dispatch back to the
// component instance that owns it.
//
// This includes:
// - the custom element host
// - host light-DOM children with Rocket-scoped expressions
// - rendered descendants inside the light/shadow mount root
//
// Without this bridge map, `@dispatchRocket(...)` would know the current
// element but not which component instance should resolve the action.
const rocketHostsByElement = new WeakMap<Element, RocketActionBridge>()

registerAction({
  name: rocketDispatchActionName,

  // `@dispatchRocket(...)` is the bridge emitted by Rocket's expression rewrite.
  // Resolve the active component from the element being evaluated and forward
  // the call into that component instance.
  apply({ el, evt, cleanups }, name: string, ...args: any[]) {
    const host = rocketHostsByElement.get(el)
    if (!host) return
    return host.dispatchRocketAction(name, el, evt, cleanups, ...args)
  },
})

// Register one custom element tag as a Rocket component and wire its prop,
// setup, render, and Datastar-bridge lifecycle into that element class.
export function rocket<
  Refs extends RefCtors = RefCtors,
  Defs extends PropDefs = PropDefs,
>(tag: string, options?: RocketDefinition<Defs, Refs>) {
  if (!tag) return
  if (!isDocumentObserverActive()) {
    pendingRocketDefinitions.set(
      tag,
      (options ?? {}) as RocketDefinition<any, any>,
    )
    ensureRocketBootstrap()
    return
  }
  const {
    manifest,
    mode,
    props,
    render,
    renderOnPropChange,
    setup,
    onFirstUpdate,
  } = options ?? {}
  if (registeredTags.has(tag) || customElements.get(tag)) {
    registeredTags.add(tag)
    return customElements.get(tag)
  }
  registeredTags.add(tag)

  const propDefs = (props?.(codecRegistry) ?? {}) as Defs
  const propNames = Object.keys(propDefs) as Array<
    keyof InferProps<Defs> & string
  >
  const attrToProp = new Map(propNames.map((name) => [kebab(name), name]))
  const componentManifest: RocketComponentManifest = {
    tag,
    props: propNames.map((name) => {
      const codec = propDefs[name]
      const codecManifest = getCodecManifest(codec)
      return {
        name,
        attribute: kebab(name),
        type: codecManifest.type,
        default: getCodecDefault(codec),
        required: false,
        values: codecManifest.values ? [...codecManifest.values] : undefined,
        docs: codecManifest.docs ? { ...codecManifest.docs } : undefined,
      }
    }),
    slots: (manifest?.slots ?? []).map((slot) => ({ ...slot })),
    events: (manifest?.events ?? []).map((event) => ({
      kind: 'event',
      ...event,
    })),
  }
  const tagScope = snake(tag)
  const getReactiveScopedPath = <T>(path: string): T | undefined => {
    let value: any = root
    for (const segment of path.split('.')) {
      if (
        value == null ||
        (typeof value !== 'object' && typeof value !== 'function') ||
        !(segment in value)
      ) {
        return
      }
      value = value[segment]
    }
    return value as T
  }
  const hasReactiveScopedPath = (path: string): boolean => {
    let value: any = root
    for (const segment of path.split('.')) {
      if (
        value == null ||
        (typeof value !== 'object' && typeof value !== 'function') ||
        !(segment in value)
      ) {
        return false
      }
      value = value[segment]
    }
    return true
  }
  const subtreeUsesScopedSignals = (
    root: ParentNode,
    signalPathBase: string,
  ): boolean => {
    const nodes =
      root instanceof Element
        ? [root, ...root.querySelectorAll('*')]
        : [...root.children, ...root.querySelectorAll('*')]
    for (const node of nodes) {
      for (const attr of node.attributes) {
        if (!attr.name.startsWith('data-')) continue
        if (
          attr.name === `data-signals:${signalPathBase}` ||
          attr.name.startsWith(`data-signals:${signalPathBase}.`)
        ) {
          return true
        }
        const colonIndex = attr.name.indexOf(':')
        if (colonIndex !== -1) {
          const key = attr.name.slice(colonIndex + 1).split('__', 1)[0]
          if (key === signalPathBase || key.startsWith(`${signalPathBase}.`)) {
            return true
          }
        }
        if (
          attr.value === signalPathBase ||
          attr.value.startsWith(`${signalPathBase}.`) ||
          attr.value.includes(`$.${signalPathBase}`)
        ) {
          return true
        }
      }
    }
    return false
  }
  const initRocketStructures = (
    root: ParentNode,
    scope: TaggedLiteralScope,
  ) => [
    ...initRocketFors(root, scope, initRocketStructures),
    ...initRocketConditionals(root, scope, initRocketStructures),
  ]

  class RocketElement extends HTMLElement {
    #instanceId = ''
    #signalPathBase = ''
    #props = Object.fromEntries(
      propNames.map((name) => [name, getCodecDefault(propDefs[name])]),
    ) as InferProps<Defs>
    #refs: RefRecord = {}
    #state: StateRecord = {}
    #propObservers: Array<PropObserverRegistration<InferProps<Defs>>> = []
    #actions = new Map<string, RocketAction<InferProps<Defs>>>()
    #cleanups: Array<() => void> = []
    #conditionalCleanups: Array<() => void> = []
    #hostConditionalCleanups: Array<() => void> = []
    #globalActionCleanups = new Map<string, () => void>()
    #datastarApplied = false
    #mounted = false
    #renderQueued = false
    #reflecting = false
    #mountRoot?: ShadowRoot | HTMLElement
    #deferredApplyIgnore = false
    #hostTreeScoped = false
    #needsSignalPathBase = false
    #pendingInitialAttrValues = new Map<string, string>()
    #lightSlotNodes?: ChildNode[]
    #rewriteScopedDataAttributes = (root: ParentNode) => {
      rewriteDataAttributes(root, {
        signalPathBase: this.#signalPathBase,
      })
      if (!this.#needsSignalPathBase) {
        this.#needsSignalPathBase = subtreeUsesScopedSignals(
          root,
          this.#signalPathBase,
        )
      }
    }
    #syncRefs = () => {
      const nextRefs: RefRecord = {}
      const roots =
        this.#mountRoot === this || !this.#mountRoot
          ? [this as ParentNode]
          : [this as ParentNode, this.#mountRoot]
      for (const root of roots) {
        const elements =
          root instanceof Element
            ? [
                ...(root.hasAttribute(rocketRefAttr) ? [root] : []),
                ...root.querySelectorAll(`[${rocketRefAttr}]`),
              ]
            : [...root.querySelectorAll(`[${rocketRefAttr}]`)]
        for (const element of elements) {
          const name = element.getAttribute(rocketRefAttr)
          if (name) nextRefs[name] = element
        }
      }
      for (const name of Object.keys(this.#refs)) {
        delete this.#refs[name]
      }
      Object.assign(this.#refs, nextRefs)
    }

    // Re-scope host-owned children after backend/DOM patch operations.
    //
    // `datastar-patch-elements` mutates the DOM first and only then emits the
    // `datastar:scope-children` hook from `patchElements.ts`. Rocket uses that
    // hook to rewrite any newly inserted host children from local `$$...`
    // expressions to the component's concrete `_rocket...` signal path before
    // the engine mutation observer applies those nodes.
    //
    // The rescoping surface depends on mode:
    // - `light` mode patches and renders directly on the host, so the host
    //   subtree itself must be rescoped and re-applied
    // - `open`/`closed` mode can still patch host-side/slotted children that
    //   live outside the shadow root, but the host element's own Datastar
    //   attributes stay in the outer page scope and must not be rewritten
    //
    // Without this rescoping pass, patched nodes can reach the engine as raw
    // `$$isOpen`, `$$clicks`, etc. and fail with `ReferenceError` before Rocket
    // gets another chance to touch them.
    #scopePatchedChildren = () => {
      if (this.#mountRoot === this) {
        this.#rewriteScopedDataAttributes(this)
        if (this.#needsSignalPathBase) {
          mergePaths([[this.#signalPathBase, {}]], { ifMissing: true })
        }

        // Host-side patching can replace a subtree that already had Rocket
        // conditionals mounted. In `light` mode that subtree is the same surface
        // as the component mount root, so stale controllers from the previous
        // branch chain must be torn down before scanning the patched content.
        //
        // Without this reset, `initRocketConditionals()` can skip the reused
        // template head because it still has a controller entry in the WeakMap,
        // leaving the freshly patched branch inert.
        for (const cleanup of this.#conditionalCleanups) cleanup()
        this.#conditionalCleanups.length = 0
        this.#conditionalCleanups.push(
          ...initRocketStructures(this, {
            signalPathBase: this.#signalPathBase,
          }),
        )
        apply(this, false)
        this.#syncRefs()
      } else {
        // In shadow mode, host-level Datastar attrs on the custom element belong
        // to the outer page scope. Only rescope/apply actual host children that
        // Rocket patched or slotted content inserted, otherwise outer
        // `data-bind:*` / `data-attr:*` on the custom-element host get renamed
        // into the component's private `_rocket.*` path after upgrade.
        for (const child of this.children) {
          this.#rewriteScopedDataAttributes(child)
        }
        if (this.#needsSignalPathBase) {
          mergePaths([[this.#signalPathBase, {}]], { ifMissing: true })
        }
        for (const cleanup of this.#hostConditionalCleanups) cleanup()
        this.#hostConditionalCleanups.length = 0
        for (const child of this.children) {
          this.#hostConditionalCleanups.push(
            ...initRocketStructures(child, {
              signalPathBase: this.#signalPathBase,
            }),
          )
          if (!(child instanceof HTMLElement || child instanceof SVGElement)) {
            continue
          }
          apply(child, false)
        }
        this.#syncRefs()
      }
    }
    #html = hypertext(
      (string) => {
        const template = document.createElement('template')
        template.innerHTML = string
        return document.importNode(template.content, true)
      },
      (fragment) => {
        this.#rewriteScopedDataAttributes(fragment)
        return fragment as DocumentFragment
      },
    )
    #svg = hypertext(
      (string) => {
        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g')
        g.innerHTML = string
        return g
      },
      (g) => {
        const fragment = document.createDocumentFragment()
        while (g.firstChild) fragment.appendChild(g.firstChild)
        this.#rewriteScopedDataAttributes(fragment)
        return fragment
      },
    )

    static get observedAttributes() {
      return [...attrToProp.keys()]
    }

    get rocketInstanceId() {
      return this.#instanceId
    }

    get rocketSignalPath() {
      return this.#signalPathBase
    }

    constructor() {
      super()
      const instanceSegment = snake(this.id.trim())
        .replace(/[^A-Za-z0-9_$]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
      this.#instanceId = instanceSegment
        ? /^[A-Za-z_$]/.test(instanceSegment)
          ? instanceSegment
          : `id_${instanceSegment}`
        : `id${++nextInstanceId}`
      this.#signalPathBase = `_rocket.${tagScope}.${this.#instanceId}`

      // Defer Datastar on the host subtree until Rocket setup has finished.
      //
      // Without this guard, the document-level engine pass can reach existing
      // light-DOM/slotted children as soon as the custom element enters the DOM.
      // Those host-provided children may still contain raw `$$...` expressions
      // that only Rocket knows how to scope. If engine evaluates a child like
      // `data-init="$$isOpen = true"` before Rocket setup rewrites or projects
      // it, execution fails with `ReferenceError: $$ is not defined`.
      //
      // Rocket removes this temporary ignore flag after setup/render complete
      // and then manually applies the correct roots in `connectedCallback()`.
      // This keeps the initial engine pass from racing ahead of Rocket's mount
      // lifecycle while preserving normal Datastar behavior once the component
      // is ready.
      //
      // Some hosts are marked before upgrade by `markPendingRocketHosts()` when
      // the page inserts raw `$$...` children before the custom element class
      // exists. We adopt/remove that marker later in `connectedCallback()`
      // instead of here because custom-element constructors cannot mutate
      // attributes on freshly created elements.

      // Opt every Rocket host into the patch-elements child-scoping hook.
      //
      // `patchElements.ts` looks for the nearest `[data-scope-children]`
      // ancestor after morphing content and dispatches `datastar:scope-children`
      // there. Rocket marks the host in `connectedCallback()` so any
      // backend-patched host children are respecified through
      // `#scopePatchedChildren()` before Datastar applies them.
      //
      // This is not just a light-DOM concern. In `open` and `closed` modes the
      // shadow tree is isolated, but host-side children and slotted content
      // still live on the custom element itself, so a backend patch can still
      // inject raw `$$...` expressions onto the host subtree.
      this.addEventListener(
        'datastar:scope-children',
        this.#scopePatchedChildren,
      )

      // Existing host children can already be present when a custom element is
      // upgraded. Scope those children in the constructor, not just in
      // `connectedCallback()`, so the document-level engine can never race
      // ahead and evaluate raw `$$...` expressions before Rocket has rewritten
      // them to the component's `_rocket...` path. In shadow mode this scopes
      // only host children, not host-level Datastar attrs, because those attrs
      // belong to the outer page binding surface.
      if (this.childNodes.length) {
        if (mode === 'light') {
          this.#rewriteScopedDataAttributes(this)
        } else {
          for (const child of this.children) {
            this.#rewriteScopedDataAttributes(child)
          }
        }
        this.#hostTreeScoped = true
      }

      // Upgraded custom elements can already have observed attributes on the
      // host. Rocket decodes those immediately here so `setup()` and the first
      // render see the final prop values right away.
      //
      // The browser then replays those same pre-existing attributes through
      // `attributeChangedCallback()` once during upgrade. Without this cache
      // Rocket would decode the same initial attribute twice, which is mostly
      // harmless but duplicates side effects like codec warnings for invalid
      // input.
      for (const [attrName, propName] of attrToProp) {
        const rawValue = this.getAttribute(attrName)
        if (rawValue !== null) {
          this.#pendingInitialAttrValues.set(attrName, rawValue)
          this.#props[propName] = decodeCodec(propDefs[propName], rawValue)
        }
      }

      // Upgraded custom elements can also already have JS props assigned on
      // the unresolved instance. Those own-properties shadow the prototype
      // accessors Rocket defines later, so replay them through the setter now.
      for (const name of propNames) {
        if (!Object.prototype.hasOwnProperty.call(this, name)) continue
        const value = (this as any)[name]
        delete (this as any)[name]
        ;(this as any)[name] = value
      }
    }

    dispatchRocketAction(
      name: string,
      el: Element | null,
      evt: Event | undefined,
      cleanups: Map<string, () => void>,
      ...args: any[]
    ) {
      const action = this.#actions.get(name)
      if (action) {
        return action(
          {
            host: this as RocketHostWithProps<InferProps<Defs>>,
            props: this.#props,
            state: this.#state,
            el,
            evt,
          },
          ...args,
        )
      }

      // Nested Rocket children should be able to call actions owned by an
      // ancestor Rocket host. Walk the composed parent chain until we find a
      // different owning Rocket instance and let that instance resolve its
      // local/global action path before we fall back globally here.
      for (let node: Element | null = this; node; ) {
        const host = rocketHostsByElement.get(node)
        const root = node.getRootNode()
        if (host && host !== this) {
          return host.dispatchRocketAction(name, el, evt, cleanups, ...args)
        }
        node =
          node.parentElement ?? (root instanceof ShadowRoot ? root.host : null)
      }

      // Rocket rewrites all `@foo(...)` calls in component-owned markup to
      // `@dispatchRocket("foo", ...)` so the component can intercept local
      // actions first. If the component does not define that action, fall back
      // to the global Datastar action registry instead of failing. This keeps
      // global helpers like `@intl(...)` working inside Rocket templates.
      const globalAction = actions[name]
      if (globalAction) {
        return globalAction(
          globalActionContext((el ?? this) as HTMLOrSVG, evt, cleanups),
          ...args,
        )
      }
      throw Error(`Undefined Rocket action: ${name}`)
    }

    #queueRender() {
      // Prop-driven rerenders are coalesced into one microtask per component.
      //
      // Multiple `#setProp()` calls in the same turn should not cause multiple
      // `render()` executions. They all flow through this queue gate so Rocket
      // recomputes the component DOM once from the latest prop snapshot.
      //
      // This queue is only for Rocket's component `render()` work. Reactive
      // Datastar directives inside the rendered DOM continue to update through
      // their own effects without enqueuing another component render.
      if (!this.#mounted || this.#renderQueued) return
      this.#renderQueued = true
      queueMicrotask(() => {
        this.#renderQueued = false
        if (this.#mounted) this.#render({})
      })
    }

    #indexActionHosts() {
      // Rocket action dispatch can originate from two different DOM surfaces:
      //
      // 1. the component's rendered mount root (light DOM or shadow DOM)
      // 2. host-provided light-DOM children that stay outside the mount root in
      //    `open`/`closed` modes but still contain Rocket-scoped expressions
      //
      // Index both surfaces so `@dispatchRocket(...)` can always resolve the
      // owning component instance from the element currently being evaluated.
      rocketHostsByElement.set(this, this)
      for (const node of this.querySelectorAll('*')) {
        rocketHostsByElement.set(node, this)
      }
      if (this.#mountRoot instanceof Element) {
        rocketHostsByElement.set(this.#mountRoot, this)
      }
      for (const node of this.#mountRoot!.querySelectorAll('*')) {
        rocketHostsByElement.set(node, this)
      }
    }

    #setProp(
      name: keyof InferProps<Defs> & string,
      nextValue: InferProps<Defs>[typeof name],
    ) {
      if (Object.is(this.#props[name], nextValue)) return
      this.#props[name] = nextValue
      const changes = { [name]: nextValue } as Partial<InferProps<Defs>>
      for (const observer of this.#propObservers) {
        if (observer.names === null || observer.names.has(name)) {
          observer.fn(this.#props, changes)
        }
      }

      // Props always update synchronously and notify `observeProps()` first.
      // Rerendering is a separate decision layered on top of that prop change.
      // When enabled, the rerender goes through `#queueRender()` so a burst of
      // prop writes still collapses to one component render.
      if (
        typeof renderOnPropChange === 'function'
          ? renderOnPropChange({
              host: this as RocketHostWithProps<InferProps<Defs>>,
              props: this.#props,
              changes,
            })
          : renderOnPropChange !== false
      ) {
        this.#queueRender()
      }
    }

    #getDefaultProp(name: keyof InferProps<Defs> & string) {
      return this.#props[name]
    }

    #reflectPropAttribute(
      name: keyof InferProps<Defs> & string,
      value: InferProps<Defs>[typeof name],
    ) {
      if (mode !== 'light' && !this.isConnected) return
      const attrName = kebab(name)
      this.#reflecting = true
      try {
        const encoded = propDefs[name].encode(value)
        if (encoded == null) {
          this.removeAttribute(attrName)
        } else {
          this.setAttribute(attrName, encoded)
        }
      } finally {
        this.#reflecting = false
      }
    }

    #setDefaultProp(
      name: keyof InferProps<Defs> & string,
      value: InferProps<Defs>[typeof name],
    ) {
      this.#setProp(name, value)
      this.#reflectPropAttribute(name, value)
    }

    #defineHostProp(name: string, descriptor: HostPropDescriptor) {
      const ownDescriptor = Object.getOwnPropertyDescriptor(this, name)
      const ownValue =
        ownDescriptor && 'value' in ownDescriptor
          ? ownDescriptor.value
          : undefined
      if (ownDescriptor) {
        delete (this as any)[name]
      }
      Object.defineProperty(this, name, {
        configurable: true,
        ...descriptor,
      })
      if (
        ownDescriptor &&
        'value' in ownDescriptor &&
        ownValue !== undefined &&
        typeof descriptor.set === 'function'
      ) {
        ;(this as any)[name] = ownValue
      }
    }

    #overrideProp<Name extends keyof InferProps<Defs> & string>(
      name: Name,
      getter?: PropOverrideGetter<InferProps<Defs>, Name>,
      setter?: PropOverrideSetter<InferProps<Defs>, Name>,
    ) {
      this.#defineHostProp(name, {
        get: getter
          ? () => getter(() => this.#getDefaultProp(name))
          : () => this.#getDefaultProp(name),
        set: setter
          ? (value: InferProps<Defs>[Name]) =>
              setter(value, (nextValue) =>
                this.#setDefaultProp(name, nextValue),
              )
          : (value: InferProps<Defs>[Name]) =>
              this.#setDefaultProp(name, value),
      })
    }

    #render(
      overrides: RenderContextOverrides<InferProps<Defs>>,
      ...args: any[]
    ) {
      if (!render) {
        this.#indexActionHosts()
        return
      }
      if (this.#datastarApplied) {
        for (const cleanup of this.#conditionalCleanups) cleanup()
        this.#conditionalCleanups.length = 0
      }
      const rendered = (
        render as (
          context: RenderContext<InferProps<Defs>>,
          ...args: any[]
        ) => RocketRenderValue
      )(
        {
          html: this.#html,
          svg: this.#svg,
          props: this.#props,
          host: this as RocketHostWithProps<InferProps<Defs>>,
          ...overrides,
        },
        ...args,
      )
      let fragment: DocumentFragment
      if (rendered instanceof DocumentFragment) {
        fragment = rendered
      } else {
        fragment = document.createDocumentFragment()
        if (rendered != null && !(rendered instanceof Node)) {
          appendComposedTemplateValue(fragment, rendered)
        } else if (rendered != null) {
          fragment.appendChild(document.createTextNode(`${rendered}`))
        }
      }
      if (this.#mountRoot === this) {
        const slots = [...fragment.querySelectorAll('slot')]
        if (slots.length) {
          // Light mode renders into the host itself, so native slot projection
          // never runs. Treat rendered <slot> elements as Rocket projection
          // markers: move the original host children into those positions and
          // scope any $$ expressions once before Datastar applies the subtree.
          //
          // This intentionally supports only the host children that existed
          // when the component first rendered. We snapshot those nodes once so
          // later rerenders can keep projecting the same nodes into the current
          // light-DOM template without reparsing or duplicating them.
          //
          // Slot matching follows the platform shape closely enough to make the
          // authored markup unsurprising:
          // - <slot name="title"> receives host children with slot="title"
          // - <slot> receives host children with no slot attribute
          // - when no children match, the slot's own fallback children render
          //
          // The browser does not do any of this in light DOM. Rocket resolves
          // the slot markers before morphing so the final light-DOM tree is the
          // flattened result rather than literal <slot> elements.
          if (!this.#lightSlotNodes) {
            const content = document.createDocumentFragment()
            content.append(...this.childNodes)
            rewriteDataAttributes(content, {
              signalPathBase: this.#signalPathBase,
            })
            this.#lightSlotNodes = [...content.childNodes]
          }
          const assigned = new Set<ChildNode>()
          for (const slot of slots) {
            const name = slot.getAttribute('name')
            const children = this.#lightSlotNodes.filter((node) => {
              if (assigned.has(node)) return false
              if (!(node instanceof Element))
                return name === null || name === ''
              const slotName = node.getAttribute('slot')
              return name === null || name === ''
                ? slotName === null || slotName === ''
                : slotName === name
            })
            if (children.length) {
              for (const child of children) assigned.add(child)
              slot.replaceWith(...children)
              continue
            }
            slot.replaceWith(...slot.childNodes)
          }
        }
      }
      morph(this.#mountRoot!, fragment, 'inner')
      if (this.#datastarApplied) {
        this.#conditionalCleanups.push(
          ...initRocketStructures(this.#mountRoot!, {
            signalPathBase: this.#signalPathBase,
          }),
        )
      }
      this.#syncRefs()
      this.#indexActionHosts()
    }

    connectedCallback() {
      if (this.#mounted) return
      this.#mounted = true

      // Custom-element constructors cannot add/remove attributes on freshly
      // created elements. Apply Rocket's host markers here instead so
      // `document.createElement(<rocket-tag>)` remains valid.
      if (this.hasAttribute(rocketDeferredIgnoreAttr)) {
        this.removeAttribute(rocketDeferredIgnoreAttr)
        this.#deferredApplyIgnore = true
      } else if (!this.hasAttribute('data-ignore')) {
        this.setAttribute('data-ignore', '')
        this.#deferredApplyIgnore = true
      }
      this.setAttribute('data-scope-children', '')
      this.setAttribute(rocketHostAttr, '')

      if (!this.#mountRoot) {
        this.#mountRoot =
          mode === 'light'
            ? this
            : (this.shadowRoot ??
              this.attachShadow({
                mode: mode === 'closed' ? 'closed' : 'open',
              }))
      }
      if (!this.#hostTreeScoped) {
        // Existing host children can contain raw $$ expressions before Rocket
        // has rendered anything. Scope that content once up front so later
        // apply passes never evaluate unscoped expressions. In shadow mode
        // this still excludes the custom-element host itself because outer
        // page bindings on the host must remain in page scope.
        if (this.#mountRoot === this) {
          this.#rewriteScopedDataAttributes(this)
        } else {
          for (const child of this.children) {
            this.#rewriteScopedDataAttributes(child)
          }
        }
        this.#hostTreeScoped = true
      }

      const localSignals = new Proxy(
        ((name, initialValue) => {
          const path = `${this.#signalPathBase}.${name}`
          mergePaths(
            [
              [
                path,
                typeof initialValue === 'function'
                  ? datastarComputed(initialValue as () => unknown)
                  : initialValue,
              ],
            ],
            { ifMissing: true },
          )
          if (!Object.prototype.hasOwnProperty.call(this.#state, name)) {
            Object.defineProperty(this.#state, name, {
              configurable: true,
              enumerable: true,
              get: () => getPath(path),
              set: (value) => {
                mergePaths([[path, value]])
              },
            })
          }
          return getPath(path)
        }) as SetupSignal,
        {
          get: (target, name, receiver) => {
            if (typeof name !== 'string' || name in target) {
              return Reflect.get(target, name, receiver)
            }

            // Setup often reads local signals before Datastar has written them.
            //
            // Walk the global root through `in` checks first so the proxy's
            // reactive `has` trap subscribes to future key creation without
            // materializing `_rocket.<tag>.<id>` or a missing local leaf as an
            // empty-string signal.
            return getReactiveScopedPath(`${this.#signalPathBase}.${name}`)
          },
          set: (_, name, value) => {
            if (typeof name !== 'string') return false
            const path = `${this.#signalPathBase}.${name}`
            if (!Object.prototype.hasOwnProperty.call(this.#state, name)) {
              Object.defineProperty(this.#state, name, {
                configurable: true,
                enumerable: true,
                get: () => getPath(path),
                set: (value) => {
                  mergePaths([[path, value]])
                },
              })
            }
            mergePaths([
              [
                path,
                typeof value === 'function'
                  ? datastarComputed(value as () => unknown)
                  : value,
              ],
            ])
            return true
          },
          has: (target, name) => {
            if (typeof name !== 'string') return name in target
            return (
              name in target ||
              hasReactiveScopedPath(`${this.#signalPathBase}.${name}`)
            )
          },
          ownKeys: (target) => [
            ...new Set([
              ...Reflect.ownKeys(target),
              ...Object.keys(
                getReactiveScopedPath<Record<string, any>>(
                  this.#signalPathBase,
                ) ?? {},
              ),
            ]),
          ],
          getOwnPropertyDescriptor: (target, name) => {
            if (Reflect.has(target, name)) {
              return Reflect.getOwnPropertyDescriptor(target, name)
            }
            if (
              typeof name === 'string' &&
              hasReactiveScopedPath(`${this.#signalPathBase}.${name}`)
            ) {
              return {
                configurable: true,
                enumerable: true,
                writable: true,
                value: getReactiveScopedPath(`${this.#signalPathBase}.${name}`),
              }
            }
          },
        },
      )
      const refs = new Proxy(
        {},
        {
          get: (_, name, receiver) =>
            typeof name === 'string'
              ? this.#refs[name]
              : Reflect.get({}, name, receiver),
          has: (_, name) => {
            if (typeof name !== 'string') return false
            return name in this.#refs
          },
          ownKeys: () => Object.keys(this.#refs),
          getOwnPropertyDescriptor: (_, name) => {
            if (typeof name === 'string' && name in this.#refs) {
              return {
                configurable: true,
                enumerable: true,
                writable: false,
                value: this.#refs[name],
              }
            }
          },
        },
      ) as Refs

      const context: SetupContext<InferProps<Defs>> = {
        props: this.#props,

        // `$` is the imperative setup alias for Datastar's global signal root.
        //
        // The name mirrors expression syntax (`$foo`) while keeping setup code
        // in plain JavaScript property access form (`$.foo`).
        $: root,

        // `$$` is the imperative setup alias for Rocket-local signals.
        //
        // The name mirrors template syntax (`$$foo`) while still being valid
        // JavaScript as a property/callable proxy namespace inside `setup`.
        $$: localSignals,
        effect: (fn) => {
          const cleanup = effect(fn)
          this.#cleanups.push(cleanup)
          return cleanup
        },
        apply: (root, merge = true) => {
          apply(root, merge)
        },
        adoptStyles: (host, ...styles) => {
          if (
            this.#mountRoot instanceof ShadowRoot &&
            'adoptedStyleSheets' in Document.prototype &&
            'replaceSync' in CSSStyleSheet.prototype
          ) {
            const sheets = styles
              .filter(Boolean)
              .map((text) => {
                const sheet = new CSSStyleSheet()
                sheet.replaceSync(text)
                return sheet
              })
            this.#mountRoot.adoptedStyleSheets = [
              ...this.#mountRoot.adoptedStyleSheets,
              ...sheets,
            ]
            return
          }

          const root =
            this.#mountRoot instanceof ShadowRoot ? this.#mountRoot : host
          for (const text of styles) {
            if (!text) continue
            const style = document.createElement('style')
            style.textContent = text
            root.prepend(style)
          }
        },
        cleanup: (fn) => {
          this.#cleanups.push(fn)
        },
        emit: ((type: string, ...rest: any[]) => {
          if (!rest.length || typeof rest[0] === 'string') {
            for (const name of [type, ...rest]) {
              this.dispatchEvent(
                new Event(name, { bubbles: true, composed: true }),
              )
            }
            return
          }

          this.dispatchEvent(
            new CustomEvent(type, {
              bubbles: true,
              composed: true,
              ...(rest[1] ?? {}),
              detail: rest[0],
            }),
          )
        }) as SetupEmit,
        emitCancellable: ((type: string, ...rest: any[]) => {
          if (!rest.length) {
            return this.dispatchEvent(
              new Event(type, {
                bubbles: true,
                composed: true,
                cancelable: true,
              }),
            )
          }

          return this.dispatchEvent(
            new CustomEvent(type, {
              bubbles: true,
              composed: true,
              cancelable: true,
              ...(rest[1] ?? {}),
              detail: rest[0],
            }),
          )
        }) as SetupEmitCancellable,

        // Expose the global Datastar signal root inside `setup`.
        //
        // This is a direct reference to the shared reactive store, not a
        // Rocket-scoped alias. Use it when setup code needs to read or mutate
        // application-level signals outside the component's private `_rocket`
        // namespace.
        // Expose global Datastar actions as imperative methods inside `setup`.
        //
        // Each call uses a component-owned cleanup map so global actions with
        // teardown behavior remain tied to the component lifecycle and are
        // released from `disconnectedCallback()`.
        actions: new Proxy(
          {},
          {
            get: (_, name: string | symbol) => {
              if (typeof name !== 'string') return
              return (...args: any[]) => {
                const action = actions[name]
                if (!action) throw Error(`Undefined action: ${name}`)
                return action(
                  globalActionContext(
                    this,
                    undefined,
                    this.#globalActionCleanups,
                  ),
                  ...args,
                )
              }
            },
          },
        ) as Record<string, (...args: any[]) => any>,
        action: (name, fn) => {
          this.#actions.set(name, fn)
        },
        observeProps: (fn, ...propNames) => {
          const registration = {
            fn,
            names: propNames.length === 0 ? null : new Set(propNames),
          }
          this.#propObservers.push(registration)
          return () => {
            const index = this.#propObservers.indexOf(registration)
            if (index >= 0) this.#propObservers.splice(index, 1)
          }
        },
        overrideProp: (name, getter, setter) => {
          this.#overrideProp(name, getter, setter)
        },
        defineHostProp: (name, descriptor) => {
          this.#defineHostProp(name, descriptor)
        },
        render: ((
          overrides: RenderContextOverrides<InferProps<Defs>>,
          ...args: any[]
        ) => {
          this.#render(overrides, ...args)
        }) as SetupRender<InferProps<Defs>>,
        host: this as RocketHostWithProps<InferProps<Defs>>,
      }
      const firstUpdateContext: FirstUpdateContext<InferProps<Defs>, Refs> = {
        ...context,
        refs: refs as unknown as InstancesOf<Refs>,
      }

      setup?.(context)
      this.#render({})
      if (this.#needsSignalPathBase) {
        mergePaths([[this.#signalPathBase, {}]], { ifMissing: true })
      }
      if (this.#deferredApplyIgnore) {
        this.removeAttribute('data-ignore')
        this.#deferredApplyIgnore = false
      }

      // In shadow mode, the host itself still belongs to the outer page scope.
      // Apply only the host element's own Datastar attrs here so late-applied
      // page bindings like `data-bind:*` can attach without rewriting the host
      // into Rocket's private `_rocket.*` scope.
      if (this.#mountRoot !== this) {
        applyElement(this)
      }

      // Apply only the component-owned render surface here.
      //
      // In `light` mode that surface is the host itself.
      // In shadow modes that surface is only the shadow root; re-applying the
      // custom-element host here would incorrectly rewrite outer-page
      // `data-bind:*` / `data-attr:*` on the host into the component's private
      // `_rocket.*` path after upgrade.
      apply(this.#mountRoot!, true)
      this.#datastarApplied = true
      this.#conditionalCleanups.push(
        ...initRocketStructures(this.#mountRoot!, {
          signalPathBase: this.#signalPathBase,
        }),
      )
      this.#syncRefs()
      const refsObserver = new MutationObserver(() => this.#syncRefs())
      refsObserver.observe(this.#mountRoot!, {
        childList: true,
        subtree: true,
      })
      if (this.#mountRoot !== this) {
        refsObserver.observe(this, {
          childList: true,
          subtree: true,
        })
      }
      this.#cleanups.push(() => refsObserver.disconnect())
      this.#indexActionHosts()
      onFirstUpdate?.(firstUpdateContext)
    }

    attributeChangedCallback(
      name: string,
      oldValue: string | null,
      newValue: string | null,
    ) {
      if (this.#reflecting) return
      const propName = attrToProp.get(name)
      if (!propName) return
      if (
        newValue !== null &&
        oldValue === null &&
        this.#pendingInitialAttrValues.get(name) === newValue
      ) {
        this.#pendingInitialAttrValues.delete(name)
        return
      }
      this.#setProp(
        propName,
        newValue === null
          ? getCodecDefault(propDefs[propName])
          : decodeCodec(propDefs[propName], newValue),
      )
    }

    disconnectedCallback() {
      mergePaths([[this.#signalPathBase, null]])
      this.removeEventListener(
        'datastar:scope-children',
        this.#scopePatchedChildren,
      )

      // Global actions called from `setup({ actions })` may have registered
      // cleanups. Run them alongside Rocket's normal setup/effect cleanups when
      // the component disconnects.
      for (const cleanup of this.#globalActionCleanups.values()) cleanup()
      this.#globalActionCleanups.clear()
      for (const cleanup of this.#hostConditionalCleanups) cleanup()
      this.#hostConditionalCleanups.length = 0
      for (const cleanup of this.#conditionalCleanups) cleanup()
      this.#conditionalCleanups.length = 0
      for (const cleanup of this.#cleanups) cleanup()
      this.#cleanups.length = 0
      for (const name of Object.keys(this.#refs)) delete this.#refs[name]
      this.#propObservers = []
      this.#actions.clear()
      this.#mounted = false
    }
    static {
      for (const name of propNames) {
        Object.defineProperty(RocketElement.prototype, name, {
          get(this: RocketElement) {
            return this.#getDefaultProp(name)
          },
          set(this: RocketElement, value: InferProps<Defs>[typeof name]) {
            this.#setDefaultProp(name, value)
          },
        })
      }
    }
  }

  Object.defineProperty(RocketElement, 'manifest', {
    configurable: false,
    enumerable: false,
    value: () =>
      cloneRocketManifestComponent(
        rocketManifestRegistry.get(tag) ?? componentManifest,
      ),
  })
  customElements.define(tag, RocketElement)
  rocketManifestRegistry.set(tag, componentManifest)
  return RocketElement
}
export type { RocketDefinition, RocketRenderValue }
